/*
 * © 2023 Vertafore, Inc. All rights reserved.
 * Reproduction and distribution without the written permission of Vertafore is prohibited.
 */
import React, { Component, ReactElement } from "react";
import { Modal, Button } from "react-bootstrap";

interface RegExTesterProps {
  showModal: boolean;
  mask?: string;
  closeModal: () => void;
}

interface RegExTesterState {
  workingMask: string;
  workingValue: string;
  isMatch: boolean;
  isValidRegEx: boolean;
  maskStyle: React.CSSProperties;
  valueStyle: React.CSSProperties;
}

class RegexTesterComponent extends Component<
  RegExTesterProps,
  RegExTesterState
> {
  constructor(props: RegExTesterProps) {
    super(props);
    this.state = {
      workingMask: this.props.mask ?? "^$",
      workingValue: "",
      isMatch: false,
      isValidRegEx: true,
      maskStyle: this.defaultStyle,
      valueStyle: this.defaultStyle,
    };
  }

  defaultStyle: React.CSSProperties = {
    backgroundColor: "white",
  };

  inValidStyle: React.CSSProperties = {
    backgroundColor: "#FF8488",
  };

  validStyle: React.CSSProperties = {
    backgroundColor: "lightgreen",
  };

  componentDidMount = async (): Promise<void> => {
    this.setState({
      workingMask: this.props.mask ?? "^$",
    });
  };

  componentDidUpdate = async (
    previousProps: RegExTesterProps
  ): Promise<void> => {
    if (previousProps.mask !== this.props.mask) {
      this.setState({
        workingMask: this.props.mask ?? "^$",
      });
    }
  };

  maskChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      workingMask: e.target.value,
    });
    this.handleTest(e.target.value, this.state.workingValue);
  };

  valueChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      workingValue: e.target.value,
    });
    this.handleTest(this.state.workingMask, e.target.value);
  };

  resetMask = (): void => {
    this.setState({
      workingMask: this.props.mask ?? "^$",
    });
    this.handleTest(this.props.mask ?? "^$", this.state.workingValue);
  };

  closeModal = (): void => {
    this.props.closeModal();
  };

  handleTest = (regEx: string, value: string): void => {
    try {
      const regexObj = new RegExp(regEx);
      if (
        regEx === null ||
        regEx.length < 2 ||
        regEx.charAt(0) !== "^" ||
        regEx.slice(-1) !== "$"
      ) {
        this.setState({
          isMatch: false,
          isValidRegEx: false,
          valueStyle: this.inValidStyle,
          maskStyle: this.inValidStyle,
        });
      } else if (regexObj.test(value)) {
        this.setState({
          isMatch: true,
          isValidRegEx: true,
          valueStyle: this.validStyle,
          maskStyle: this.validStyle,
        });
      } else {
        this.setState({
          isMatch: false,
          isValidRegEx: true,
          valueStyle: this.inValidStyle,
          maskStyle: this.validStyle,
        });
      }
    } catch (error) {
      console.log("Invalid RegEx: ", regEx, error);
      this.setState({
        isMatch: false,
        isValidRegEx: false,
        valueStyle: this.inValidStyle,
        maskStyle: this.inValidStyle,
      });
    }
  };

  render = (): ReactElement => {
    return (
      <Modal size="sm" show={this.props.showModal} className="mt-5">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header d-block text-center">
              <h5 className="modal-title">RegEx Tester</h5>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <div>
                  <input
                    type="text"
                    name="mask"
                    style={this.state.maskStyle}
                    className="form-control"
                    placeholder="mask"
                    value={this.state.workingMask}
                    onChange={this.maskChange}
                  />
                </div>
                <Button
                  className="float-right"
                  onClick={this.resetMask}
                  title="Reset mask."
                >
                  R
                </Button>
              </div>
              <div className="form-group">
                <div>
                  <input
                    type="text"
                    name="value"
                    style={this.state.valueStyle}
                    placeholder="Value to evaluate"
                    className="form-control"
                    value={this.state.workingValue}
                    onChange={this.valueChange}
                  />
                </div>
              </div>
              <div className="text-xs-center">
                <div>
                  <Button
                    variant="secondary"
                    onClick={this.closeModal}
                    className="float-left"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  };
}

export default RegexTesterComponent;
